// 굿즈 관리 탭(docs/build/16 §3) — 운영자가 폼 하나로 상품을 등록·수정·게시한다.
//
// 설계 메모:
// - 폼 필드는 §4-3 `synthesize` 출력 스키마와 **1:1**. P2(AI 초안)는 여기 setState 만 연결하면 끝난다.
// - 검증은 shared 단일 정의(validateGoodsProduct)를 **폼에서도** 쓴다 → 게시 눌러야 알게 되지 않고 즉시 표시.
// - 게시는 문서 전체(엣지가 문서 단위) — 목록을 통째로 다루는 운영 UX와 일치.
import {
  useEffect,
  useMemo,
  useState } from "react";
import {
  GOODS_BADGES,
  GOODS_LIMITS,
  goodsIdFromName,
  goodsImageSlot,
  validateGoodsProduct,
  goodsPlacementStatus,
  storeLinks,
  placeGoodsInMap,
  type TiledMap,
  type GoodsBadge,
  type GoodsIssue,
  type GoodsProduct,
  type GoodsStore,
  type AssetSpec,
  storeRowsOf,
  goodsDeletePrompt,
} from "@greg-chick/shared";
import {
  loadGoodsDoc,
  loadManifest,
  publishGoods,
  publishAssets,
  uploadAsset,
  draftGoods,
  goodsAiProviders,
  loadZoneMap,
  publishZoneMap,
  type Manifest,
  type ManifestEntry,
} from "../lib/contentApi";
import { GoodsPreviewFrame } from "./GoodsPreviewFrame";
import { SlotUploader } from "./SlotUploader";
import { saveAssetUrl } from "../lib/download";
import { ActionButton } from "./ActionButton";
import { useRegisterSave } from "../lib/saveBar";
import { withEditing } from "../lib/goodsEdit";

// 굿즈 마켓 목록(/goods) 상단 배너 — 상품이 아니라 페이지 장식이라 상품 폼과 분리한다.
// content/asset-slots.json 의 goods.market.banner 와 같은 규격(운영자가 두 곳을 맞출 필요 없게 여기 고정).
const BANNER_SLOT = "goods.market.banner";
const BANNER_SPEC: AssetSpec = { formats: ["webp", "png", "jpg"], maxLongEdgePx: 2048 };
import { QrPrint } from "./QrPrint";
import type { AiProvider, AiProviderOption } from "@greg-chick/shared";

// AI 초안 진행 단계(docs/build/16 §4-8) — 7콜 ~15초는 침묵하면 멈춘 것처럼 보인다.
const AI_STAGES = ["사진 살펴보는 중", "문구 쓰는 중 (아이 · 보호자 · 세계관)", "검수 중 (안전 · 사실)", "정리하는 중"];

const EMPTY: GoodsProduct = { id: "", name_ko: "", images: [] };

/** 남은 글자수 — 상한을 넘겨야 알게 되는 대신 쓰는 동안 보이게. */
function Counter({ value, max }: { value: string | undefined; max: number }) {
  const n = value?.length ?? 0;
  return (
    <span className="goods-admin__count" data-over={n > max ? "true" : undefined}>
      {n}/{max}
    </span>
  );
}

/** 이미지 슬롯 → 미리보기 URL(매니페스트). 업로드 직후엔 아직 매니페스트에 없어 로컬 URL을 겹쳐 쓴다. */
function useImageUrls(manifest: Manifest | null, fresh: Record<string, string>) {
  return useMemo(
    () => (slot: string) => fresh[slot] ?? manifest?.assets?.[slot]?.url,
    [manifest, fresh],
  );
}

export function GoodsManager() {
  const [products, setProducts] = useState<GoodsProduct[]>([]);
  const [editing, setEditing] = useState<GoodsProduct | null>(null);
  const [originalId, setOriginalId] = useState<string | null>(null); // 수정 중인 상품(중복 id 검사에서 자기 제외)
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [fresh, setFresh] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [providers, setProviders] = useState<AiProviderOption[]>([]); // 키 있는 공급자만. 비면 AI 기능 자체를 안 그린다
  const [provider, setProvider] = useState<AiProvider | "">("");
  const [aiStage, setAiStage] = useState<number>(-1); // -1 = 미실행
  const [goodsMap, setGoodsMap] = useState<TiledMap | null>(null); // 굿즈 존 맵(진열 상태 대조용)
  const urlOf = useImageUrls(manifest, fresh);

  useEffect(() => {
    void (async () => {
      const [doc, m, ps, gmap] = await Promise.all([
        loadGoodsDoc(),
        loadManifest(),
        goodsAiProviders(),
        loadZoneMap("goods"),
      ]);
      setProducts(doc.products);
      setManifest(m);
      setProviders(ps);
      setProvider(ps[0]?.id ?? ""); // 첫 공급자를 기본 선택(운영자가 매번 고르지 않게)
      setGoodsMap(gmap as TiledMap | null);
    })();
  }, []);

  // 문서엔 있는데 굿즈 존에 진열대가 없는 상품 = 월드에서 안 보이는 것.
  const unplaced = goodsMap ? goodsPlacementStatus(products, goodsMap).unplaced : [];
  /**
   * 🛒 **살 수 있는 곳이 아직 없는 상품.**
   *
   * 🔴 목록에서는 이게 **안 보였다.** 「진열 안 됨」은 경고해 주면서, 정작 아이가 열었을 때
   * 「스토어 준비 중이에요」만 보이는 상품은 **하나하나 열어 봐야** 알 수 있었다.
   * 라이브에 여섯 개가 그 상태로 있었고(2026-09-04 실측), 개수만 아는 상태로는 어느
   * 것인지 찾느라 여섯 번을 열어야 한다.
   *
   * 판정은 **아이 화면이 읽는 것과 같은 함수**(`storeLinks`) — 여기서 다시 짜면
   * 「관리자에선 있다는데 아이 화면은 준비 중」이 된다.
   */
  const noStore = products.filter((p) => storeLinks(p).length === 0);

  /** 미배치 상품을 존 빈 칸에 자동 진열 → 맵 재게시. 실패는 던져 ActionButton 이 표시. */
  async function placeInZone() {
    if (!goodsMap) throw new Error("굿즈 존 맵을 불러오지 못했어요.");
    const { map, placed, skipped } = placeGoodsInMap(products, goodsMap);
    if (!placed.length) throw new Error("진열할 빈 자리가 없어요. 굿즈 존을 넓히거나 정리해 주세요.");
    await publishZoneMap("goods", map);
    setGoodsMap(map);
    setNote(
      `🛍️ ${placed.length}개를 굿즈 마켓 존에 진열했어요. 월드에 바로 나타나요.` +
        (skipped.length ? ` (빈 자리가 모자라 ${skipped.length}개는 못 놓았어요 — 존을 넓혀주세요.)` : ""),
    );
  }

  /**
   * AI 초안(docs/build/16 §4) — 초안일 뿐이다. 폼을 채우기만 하고 게시하지 않는다.
   * 단계 표시는 실제 서버 진행이 아니라 예상 소요 기반 연출이다(엣지가 단계별로 스트리밍하지 않으므로).
   * 정확한 진행을 보여주려면 SSE 가 필요한데, 15초짜리 작업에 그 복잡도는 과하다.
   */
  async function runAiDraft() {
    if (!editing) return;
    setBusy(true);
    setNote(null);
    setAiStage(0);
    const tick = window.setInterval(() => setAiStage((s) => (s < AI_STAGES.length - 1 ? s + 1 : s)), 4000);
    try {
      const out = await draftGoods({
        name_ko: editing.name_ko,
        imageSlots: editing.images,
        ...(provider ? { provider } : {}),
      });
      setEditing((cur) =>
        cur
          ? {
              ...cur,
              tagline_child_ko: out.draft.tagline_child_ko || cur.tagline_child_ko,
              desc_guardian_ko: out.draft.desc_guardian_ko || cur.desc_guardian_ko,
              badge: (out.draft.badge as typeof cur.badge) ?? cur.badge,
              tags: out.draft.tags.length ? out.draft.tags : cur.tags,
            }
          : cur,
      );
      const flagged = [
        out.review.safety === "revise" ? "표현" : null,
        out.review.factuality === "revise" ? "사실" : null,
      ].filter(Boolean);
      setNote(
        `${out.providerLabel} 초안을 넣었어요 — 확인하고 고쳐 주세요.${flagged.length ? ` (검수에서 ${flagged.join("·")} 지적이 있어 반영됨)` : ""}` +
          (out.draft.notes.length ? `\n· ${out.draft.notes.join("\n· ")}` : ""),
      );
    } catch (e) {
      setNote((e as Error).message); // 사람이 읽을 문장은 contentApi 에서 만든다
    } finally {
      window.clearInterval(tick);
      setAiStage(-1);
      setBusy(false);
    }
  }

  // 폼 즉시 검증(게시 버튼을 눌러야 알게 되지 않도록). 자기 자신은 중복 id 검사에서 제외.
  const issues: GoodsIssue[] = useMemo(() => {
    if (!editing) return [];
    const others = products.filter((p) => p.id !== originalId).map((p) => p.id);
    return validateGoodsProduct(editing, others);
  }, [editing, products, originalId]);
  const issueOf = (field: string) => issues.find((i) => i.field === field)?.message;

  function startNew() {
    setEditing({ ...EMPTY });
    setOriginalId(null);
    setNote(null);
  }
  function startEdit(p: GoodsProduct) {
    setEditing({ ...p });
    setOriginalId(p.id);
    setNote(null);
  }

  /** 상품명 → id 자동 제안. 한글 전용이면 item_N 으로 떨어지므로 운영자가 고칠 수 있게 필드는 열어둔다. */
  function onName(name: string) {
    if (!editing) return;
    const autoId = !originalId && !editing.id ? goodsIdFromName(name, products.map((p) => p.id)) : editing.id;
    setEditing({ ...editing, name_ko: name, id: autoId });
  }

  async function onFiles(files: FileList | null) {
    if (!editing || !files?.length) return;
    if (!editing.id) {
      setNote("상품명을 먼저 입력해 주세요(id가 만들어져야 이미지 슬롯이 정해져요).");
      return;
    }
    setBusy(true);
    try {
      const next = [...editing.images];
      for (const file of Array.from(files).slice(0, GOODS_LIMITS.images - next.length)) {
        const slot = goodsImageSlot(editing.id, next.length + 1);
        const url = await uploadAsset(slot, file);
        next.push(slot);
        setFresh((f) => ({ ...f, [slot]: url }));
      }
      setEditing({ ...editing, images: next });
      setNote(`이미지 ${next.length}장 올렸어요. 아직 게시 전이에요.`);
    } catch (e) {
      // 업로드 실패는 삼키지 않고 운영자에게 그대로 보인다(토큰 만료·용량 초과 등 사람이 조치할 문제).
      setNote(`업로드 실패: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  function removeImage(slot: string) {
    if (!editing) return;
    setEditing({ ...editing, images: editing.images.filter((s) => s !== slot) });
  }

  /** 이미지 순서 이동 — 첫 장이 대표라 순서가 실제 노출을 바꾼다(그래서 조정 수단이 필요). */
  function moveImage(i: number, dir: -1 | 1) {
    if (!editing) return;
    const j = i + dir;
    if (j < 0 || j >= editing.images.length) return;
    const next = [...editing.images];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setEditing({ ...editing, images: next });
  }

  /**
   * 살 수 있는 곳 편집 줄 — **편집 중에는 빈 줄도 그대로 둔다.**
   *
   * 주소를 지우자마자 줄이 사라지면 다시 적을 칸이 없어진다(운영자가 주소를 바꾸는 가장 흔한 동작이
   * "지우고 다시 붙여넣기"다). 그래서 화면에서는 남기고, `apply()` 에서 빈 줄을 걸러 저장한다.
   *
   * 옛 형식(store_url 하나)으로 저장된 상품은 열 때 첫 줄로 옮겨 담는다 — 운영자 눈에는
   * 그냥 첫 줄에 주소가 들어 있는 것으로 보인다.
   */
  // 읽는 곳과 저장하는 곳이 **같은 함수**를 본다(shared storeRowsOf) — 갈리면 주소가 사라진다
  const storeRows: GoodsStore[] = editing ? storeRowsOf(editing) : [];

  function setStoreRow(i: number, patch: Partial<GoodsStore>) {
    if (!editing) return;
    const next = storeRows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    // 첫 줄로 옮겨 담았으면 옛 필드는 비운다 — 두 곳에 같은 주소가 남으면 나중에 헷갈린다
    setEditing({ ...editing, stores: next, store_url: undefined });
  }
  function addStoreRow() {
    if (!editing || storeRows.length >= GOODS_LIMITS.stores) return;
    setEditing({ ...editing, stores: [...storeRows, { url: "" }], store_url: undefined });
  }
  function removeStoreRow(i: number) {
    if (!editing) return;
    const next = storeRows.filter((_, idx) => idx !== i);
    setEditing({ ...editing, stores: next.length ? next : [{ url: "" }], store_url: undefined });
  }

  /** 폼 → 목록 반영(아직 게시 아님). 게시는 [게시] 버튼에서 문서 전체로. */
  function apply() {
    if (!editing || issues.length) return;
    /**
     * 빈 줄은 저장하지 않는다 — 화면에서는 다시 적을 칸으로 남겨 두지만 문서에는 안 넣는다.
     *
     * 🔴 예전엔 여기서 `editing.stores` 를 봤다. 그러면 **옛 형식(store_url 하나)으로 저장된
     * 상품을 열어 이름만 고치고 저장했을 때 판매 링크가 통째로 사라졌다** —
     * 화면 첫 줄에 그 주소가 보이는 채로. 아무 오류도 안 나고 굿즈가 조용히
     * 「스토어 준비 중」이 된다. 화면이 읽는 것과 **같은 함수**를 본다.
     */
    // 빈 줄 거르기·옛 store_url 옮기기는 lib/goodsEdit 한 곳 — 게시·임시저장도 같은 함수를 쓴다
    setProducts((prev) => withEditing(prev, editing, originalId));
    setEditing(null);
    setOriginalId(null);
    setNote("목록에 반영했어요. 게시해야 웹에 보여요.");
  }

  function removeProduct(id: string) {
    /**
     * 🔴 **지우는 것은 목록 한 줄이고, 사진은 매니페스트에 남는다.**
     * 남는 것 자체는 나쁘지 않다(같은 이름으로 다시 만들면 그대로 붙는다) —
     * 나쁜 것은 **모르는 것**이다. 책에서 똑같은 일이 있었다(지운 책의 파일 31개가 남았다).
     */
    const p = products.find((x) => x.id === id);
    const shots = (p?.images ?? []).length;
    if (!window.confirm(goodsDeletePrompt(p?.name_ko || id, shots))) return;
    setProducts((prev) => prev.filter((x) => x.id !== id));
  }

  // 배너 업로드 결과를 fresh 에 실어 두면 아래 publish() 가 상품과 함께 매니페스트로 게시한다(별도 게시 버튼 불필요).
  function onBannerChange(key: string, entry: ManifestEntry | null) {
    setFresh((f) => {
      const next = { ...f };
      if (entry?.url) next[key] = entry.url;
      else delete next[key];
      return next;
    });
  }

  async function publish() {
    /**
     * 🔴 **편집 칸이 열려 있으면 그것도 같이 올린다**(2026-10-21 · lib/goodsEdit 주석).
     * 예전엔 「목록에 반영」을 안 누르고 올리면 고친 것이 빠진 채 「✅ 웹에 올렸어요」가 떴다.
     * 칸에 고칠 곳이 있으면 올리지 않고 **실패로** 알린다 — 반쯤 쓴 상품이 아이 화면에 나가면 안 된다.
     */
    if (editing && issues.length) {
      const say = `수정 중인 「${editing.name_ko || editing.id || "새 굿즈"}」에 고칠 곳이 ${issues.length}군데 있어요 — 고친 뒤 다시 올려 주세요.`;
      setNote(say);
      throw new Error(say);
    }
    const list = withEditing(products, editing, originalId);
    setBusy(true);
    setNote(null);
    try {
      // 이미지 URL은 업로드 시 매니페스트에 자동 등록되지 않으므로 함께 게시한다(슬롯 → URL).
      const assets = { ...(manifest?.assets ?? {}) };
      let dirty = false;
      for (const [slot, url] of Object.entries(fresh)) {
        assets[slot] = { ...(assets[slot] ?? {}), url };
        dirty = true;
      }
      if (dirty) await publishAssets(assets);

      const out = await publishGoods({ products: list });
      // 올린 것이 곧 목록이다 — 편집 칸은 닫는다(열어 두면 다음에 같은 것을 또 합친다)
      setProducts(list);
      setEditing(null);
      setOriginalId(null);
      setFresh({});
      setManifest(await loadManifest());
      setNote(`게시 완료 — v${out.version}, 상품 ${out.count}개. 웹에 바로 반영돼요.`);
    } catch (e) {
      const err = e as Error & { issues?: GoodsIssue[] };
      const say = err.issues?.length
        ? `게시 거부: ${err.issues.map((i) => `${i.id ?? ""} ${i.field} — ${i.message}`).join(" / ")}`
        : err.message === "401"
          ? "토큰이 만료됐어요. 다시 로그인해 주세요."
          : `게시 실패: ${err.message}`;
      setNote(say);
      // 🔴 저장 바에도 실패로 알린다 — 안 던지면 「✅ 웹에 올렸어요」가 뜬다(2026-09-18)
      throw new Error(say);
    } finally {
      setBusy(false);
    }
  }
  // 저장은 화면 아래 고정 바 하나로(어느 화면이든 같은 자리·같은 버튼).
  useRegisterSave({
    id: "goods",
    label: "굿즈",
    save: publish,
    // 💾 웹에 올리지 않고 하던 일만 — 상품을 여러 개 고치는 동안 창이 닫혀도 남게.
    // 편집 칸에 쓴 것도 같이 남긴다 — 단 고칠 곳이 없을 때만(「＋ 새 굿즈」만 누른 빈 칸이 목록에 끼면
    // 되살린 뒤 게시가 그 빈 상품 때문에 통째로 거부된다)
    draft: {
      data: () => (issues.length ? products : withEditing(products, editing, originalId)),
      restore: (d) => setProducts(d as GoodsProduct[]),
    },
  });

  return (
    <section className="panel">
      <header className="panel__head">
        <div className="row">
          <button type="button" className="btn" onClick={startNew} disabled={busy}>
            ＋ 새 굿즈
          </button>
        </div>
      </header>
      <p className="hint">
        구매·배송은 전부 외부 스토어에서 이뤄져요(사이트는 개인정보를 받지 않아요). 여기 적는 건 <b>보여줄 정보</b>뿐이에요.
      </p>
      {note ? <p className="note">{note}</p> : null}

      {/* 마켓 배너 — /goods 목록 페이지 맨 위 큰 그림. 상품과 별개(페이지 장식)이라 접이식으로 분리.
          여기서 올린 뒤 위의 [게시]를 누르면 상품과 함께 반영된다(배너 전용 게시 버튼을 두지 않는다). */}
      <details className="goods-admin__banner">
        <summary>
          🏮 마켓 배너 <span className="hint">굿즈 목록 페이지 맨 위 큰 그림 — 안 올리면 등불 기본 배경이 나와요</span>
        </summary>
        <SlotUploader
          slotKey={BANNER_SLOT}
          label="굿즈 마켓 배너 (가로 와이드, 권장 16:7)"
          spec={BANNER_SPEC}
          entry={fresh[BANNER_SLOT] ? { url: fresh[BANNER_SLOT] } : manifest?.assets?.[BANNER_SLOT]}
          alt="그레그와 병아리의 경주 굿즈 마켓"
          onChange={onBannerChange}
        />
      </details>

      {/* 존 진열 상태 — "굿즈를 만들었는데 월드에 안 보인다"를 잡아준다(docs/build/16 §5) */}
      {unplaced.length ? (
        <div className="goods-admin__placement">
          <span>
            ⚠️ {unplaced.length}개 상품이 <b>굿즈 마켓 존에 진열되지 않았어요</b> — 지금은 월드에서 안 보여요.
          </span>
          <ActionButton
            className="btn btn--primary"
            busyLabel="진열 중…"
            doneLabel="진열함"
            disabled={busy}
            onAction={placeInZone}
          >
            빈 자리에 진열하기
          </ActionButton>
        </div>
      ) : products.length ? (
        <p className="hint">✅ 등록한 굿즈가 모두 굿즈 마켓 존에 진열돼 있어요.</p>
      ) : null}

      {/**
        * 🔴 **개수만 주면 못 찾는다.** 갇힌 지도 오브젝트에서 배운 것과 같다 —
        * 이름을 대야 운영자가 바로 그것을 연다. 주소는 우리가 못 만들어 주므로
        * (스마트스토어에서 상품을 먼저 만들어야 한다) 여는 것까지가 우리 몫이다.
        */}
      {noStore.length ? (
        <div className="goods-admin__placement">
          <span>
            🛒 <b>{noStore.length}개 상품에 살 수 있는 곳이 없어요</b> — 아이가 열면
            「스토어 준비 중이에요」만 보여요.{" "}
            {noStore.map((p) => (
              <button
                key={p.id}
                type="button"
                className="btn btn--sm"
                onClick={() => startEdit(p)}
              >
                {p.name_ko || p.id}
              </button>
            ))}
          </span>
        </div>
      ) : null}

      {/* 목록 */}
      <div className="goods-admin__list">
        {products.length === 0 ? <p className="hint">아직 등록한 굿즈가 없어요. ‘＋ 새 굿즈’로 시작해 보세요.</p> : null}
        {products.map((p) => (
          <div key={p.id} className="goods-admin__card">
            {urlOf(p.images[0] ?? "") ? (
              <img src={urlOf(p.images[0]!)} alt="" />
            ) : (
              <span className="goods-admin__thumb-empty" aria-hidden="true">🎁</span>
            )}
            <div className="goods-admin__card-body">
              <strong>{p.name_ko}</strong>
              <span className="hint">
                {p.id}
                {p.price !== undefined ? ` · ₩${p.price.toLocaleString("ko-KR")}` : ""}
                {p.soldout ? " · 품절" : ""}
              </span>
            </div>
            <div className="row">
              <button type="button" className="btn" onClick={() => startEdit(p)}>
                수정
              </button>
              <button type="button" className="btn" onClick={() => removeProduct(p.id)}>
                삭제
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* 폼 + 실시간 미리보기(2단) — 운영자가 보는 것과 아이가 보는 것이 같아야 한다 */}
      {editing ? (
        <div className="goods-admin__edit">
        <div className="goods-admin__form">
          <h3>{originalId ? `수정 — ${originalId}` : "새 굿즈 만들기"}</h3>

          <label>
            상품명 *
            <input value={editing.name_ko} onChange={(e) => onName(e.target.value)} placeholder="그레그 첨성대 키링" />
          </label>
          {issueOf("name_ko") ? <p className="err">{issueOf("name_ko")}</p> : null}

          <label>
            id (주소·이미지 이름에 쓰여요 — 영문 권장)
            <input value={editing.id} onChange={(e) => setEditing({ ...editing, id: e.target.value })} placeholder="star_keyring" />
          </label>
          {issueOf("id") ? <p className="err">{issueOf("id")}</p> : null}

          <label>
            이미지 * (최대 {GOODS_LIMITS.images}장, 첫 장이 대표)
            <input type="file" accept="image/*" multiple disabled={busy} onChange={(e) => void onFiles(e.target.files)} />
          </label>
          <div className="goods-admin__imgs">
            {editing.images.map((slot, i) => (
              <div key={slot} className="goods-admin__img">
                {urlOf(slot) ? <img src={urlOf(slot)} alt="" /> : <span>…</span>}
                {i === 0 ? <span className="goods-admin__lead">대표</span> : null}
                {/* ⬇ 인쇄·상세페이지에 다시 쓰려면 올린 사진을 꺼낼 수 있어야 한다.
                    여기는 자리가 여럿이라 SlotUploader 를 안 써서 따로 붙인다. */}
                <button
                  type="button"
                  className="goods-admin__img-get"
                  onClick={() => void saveAssetUrl(slot, urlOf(slot)!)}
                  disabled={!urlOf(slot)}
                  title="이 사진을 내 컴퓨터에 저장해요"
                  aria-label={`${slot} 내려받기`}
                >
                  ⬇
                </button>
                <button
                  type="button"
                  className="goods-admin__img-del"
                  onClick={() => removeImage(slot)}
                  aria-label={`${slot} 제거`}
                >
                  ✕
                </button>
                <div className="goods-admin__img-move">
                  <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0} aria-label="앞으로">
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={() => moveImage(i, 1)}
                    disabled={i === editing.images.length - 1}
                    aria-label="뒤로"
                  >
                    ›
                  </button>
                </div>
              </div>
            ))}
          </div>
          {issueOf("images") ? <p className="err">{issueOf("images")}</p> : null}

          {/* AI 초안 — 키가 없으면 아예 안 보인다(없는 기능을 보여주지 않기). 초안일 뿐, 게시는 사람이. */}
          {providers.length ? (
            <div className="goods-admin__ai">
              <div className="row">
                <button
                  type="button"
                  className="btn"
                  onClick={() => void runAiDraft()}
                  disabled={busy || !editing.name_ko || !editing.images.length}
                  title={!editing.images.length ? "사진을 보고 쓰기 때문에 이미지가 먼저 필요해요" : undefined}
                >
                  ✨ AI 초안 만들기
                </button>
                {/* 키가 있는 공급자만 나온다. 1개뿐이면 고를 게 없으니 라벨만 보여준다. */}
                {providers.length > 1 ? (
                  <label className="goods-admin__ai-pick">
                    <span className="hint">AI</span>
                    <select
                      value={provider}
                      onChange={(e) => setProvider(e.target.value as AiProvider)}
                      disabled={busy}
                    >
                      {providers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <span className="hint">{providers[0]?.label}</span>
                )}
              </div>
              {/* 호환 계층 등 공급자별 주의는 고른 것만 보여준다(다 띄우면 아무도 안 읽는다) */}
              {providers.find((p) => p.id === provider)?.note ? (
                <p className="hint">⚠️ {providers.find((p) => p.id === provider)!.note}</p>
              ) : null}
              {aiStage >= 0 ? (
                <ol className="goods-admin__ai-steps" aria-live="polite">
                  {AI_STAGES.map((label, i) => (
                    <li key={label} data-state={i < aiStage ? "done" : i === aiStage ? "now" : "todo"}>
                      <span aria-hidden="true">{i < aiStage ? "✓" : i === aiStage ? "⣾" : "·"}</span> {label}
                    </li>
                  ))}
                </ol>
              ) : (
                <span className="hint">사진과 상품명을 보고 아이·보호자 문구 초안을 써줘요. 반드시 확인 후 고쳐 주세요.</span>
              )}
            </div>
          ) : null}

          <div className="row">
            <label>
              가격(원)
              <input
                type="number"
                value={editing.price ?? ""}
                onChange={(e) =>
                  setEditing({ ...editing, price: e.target.value === "" ? undefined : Number(e.target.value) })
                }
                placeholder="8900"
              />
            </label>
            <label>
              뱃지
              <select
                value={editing.badge ?? ""}
                onChange={(e) => setEditing({ ...editing, badge: (e.target.value || undefined) as GoodsBadge | undefined })}
              >
                <option value="">없음</option>
                {GOODS_BADGES.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </label>
            <label className="goods-admin__check">
              <input
                type="checkbox"
                checked={editing.soldout ?? false}
                onChange={(e) => setEditing({ ...editing, soldout: e.target.checked || undefined })}
              />
              품절
            </label>
          </div>
          {issueOf("price") ? <p className="err">{issueOf("price")}</p> : null}

          <label>
            <span className="goods-admin__label">아이 한 줄 <em>크게 보여요</em><Counter value={editing.tagline_child_ko} max={GOODS_LIMITS.tagline} /></span>
            <input
              value={editing.tagline_child_ko ?? ""}
              onChange={(e) => setEditing({ ...editing, tagline_child_ko: e.target.value || undefined })}
              placeholder="별을 담은 첨성대 키링이에요!"
            />
          </label>
          {issueOf("tagline_child_ko") ? <p className="err">{issueOf("tagline_child_ko")}</p> : null}

          <label>
            <span className="goods-admin__label">보호자 설명 <em>소재·크기·주의사항</em><Counter value={editing.desc_guardian_ko} max={GOODS_LIMITS.desc} /></span>
            <textarea
              rows={3}
              value={editing.desc_guardian_ko ?? ""}
              onChange={(e) => setEditing({ ...editing, desc_guardian_ko: e.target.value || undefined })}
              placeholder="스테인리스+아크릴, 약 5cm. 작은 부품이 있어 6세 이하는 보호자와 함께 사용해 주세요."
            />
          </label>
          {issueOf("desc_guardian_ko") ? <p className="err">{issueOf("desc_guardian_ko")}</p> : null}

          {/* 살 수 있는 곳 — 최대 5곳. **넣은 만큼만** 아이 화면에 버튼이 생긴다.
              옛 형식(store_url 하나)으로 저장된 상품은 열 때 첫 줄로 옮겨 담는다(아래 storeRows). */}
          <fieldset className="stores">
            <legend>살 수 있는 곳 (최대 {GOODS_LIMITS.stores}곳)</legend>
            <p className="hint">
              넣은 <b>개수만큼 버튼</b>이 생겨요. 한 곳만 넣으면 버튼도 하나예요. 비워 두면 아이 화면에
              &ldquo;스토어 준비 중이에요&rdquo;로 보여요.
            </p>
            {storeRows.map((row, i) => (
              <div className="stores__row" key={i}>
                <input
                  className="stores__label"
                  value={row.label ?? ""}
                  maxLength={GOODS_LIMITS.storeLabel}
                  placeholder={`버튼 이름 (예: 스마트스토어)`}
                  onChange={(e) => setStoreRow(i, { label: e.target.value })}
                />
                <input
                  className="stores__url"
                  value={row.url}
                  placeholder="https://smartstore.naver.com/..."
                  onChange={(e) => setStoreRow(i, { url: e.target.value })}
                />
                <button
                  type="button"
                  className="stores__del"
                  title="이 줄 지우기"
                  onClick={() => removeStoreRow(i)}
                >
                  ✕
                </button>
                {issueOf(`stores.${i}`) ? <p className="err">{issueOf(`stores.${i}`)}</p> : null}
              </div>
            ))}
            {storeRows.length < GOODS_LIMITS.stores ? (
              <button type="button" className="btn btn--sm" onClick={addStoreRow}>
                + 살 수 있는 곳 추가
              </button>
            ) : (
              <p className="hint">{GOODS_LIMITS.stores}곳을 다 채웠어요.</p>
            )}
          </fieldset>
          {issueOf("stores") ? <p className="err">{issueOf("stores")}</p> : null}
          <p className="hint">유입 분석용 utm 파라미터는 웹에서 자동으로 붙여요. 그냥 상품 주소만 넣으세요.</p>

          <div className="row">
            <button type="button" className="btn btn--primary" onClick={apply} disabled={busy || issues.length > 0}>
              목록에 반영
            </button>
            <button type="button" className="btn" onClick={() => setEditing(null)} disabled={busy}>
              취소
            </button>
          </div>
          {issues.length ? <p className="hint">고칠 곳이 {issues.length}군데 있어요.</p> : null}

          {editing.id ? (
            <div className="goods-qr">
              <p className="hint">🛍 <b>상품 QR</b> — 굿즈 포장·매장 안내물에 붙이면 스캔 시 이 상품 상세가 열려요(게시 후 동작).</p>
              <QrPrint path={`/goods/${encodeURIComponent(editing.id)}`} title={editing.name_ko || "굿즈"} caption="휴대폰으로 스캔하면 상품 상세가 열려요" />
            </div>
          ) : null}
        </div>

        {/* 실시간 미리보기 — 웹의 실제 컴포넌트를 iframe 으로. 입력하는 동안 그대로 반영된다. */}
        <GoodsPreviewFrame
          product={{
            ...editing,
            imageUrls: editing.images.map((s) => urlOf(s)).filter((u): u is string => !!u),
          }}
        />
        </div>
      ) : null}
    </section>
  );
}
